import { useAuthFrontendApis } from '@propelauth/frontend-apis-react';
import { useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';

interface UserPropertiesFormProps {
  onSuccess: () => void;
  onError: (error: string) => void;
}

export function UserPropertiesForm({ onSuccess, onError }: UserPropertiesFormProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ firstName?: string; lastName?: string; general?: string }>(
    {},
  );

  const { updateUserMetadata } = useAuthFrontendApis();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});

    if (!firstName.trim()) {
      setErrors({ firstName: 'First name is required' });
      setIsLoading(false);
      return;
    }

    if (!lastName.trim()) {
      setErrors({ lastName: 'Last name is required' });
      setIsLoading(false);
      return;
    }

    try {
      const response = await updateUserMetadata({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      });

      await response.handle({
        success: () => {
          onSuccess();
        },
        badRequest: error => {
          const errorMessages = error.user_facing_errors;
          setErrors({
            firstName: errorMessages?.first_name,
            lastName: errorMessages?.last_name,
            general: error.user_facing_error || 'Please check your input and try again',
          });
        },
        unexpectedOrUnhandled: error => {
          onError(error.user_facing_error || 'An unexpected error occurred. Please try again.');
        },
      });
    } catch (error) {
      onError('Failed to update your information. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold text-neutral-950 mb-2">Complete Your Profile</h3>
        <p className="text-neutral-600 text-sm">
          We need a few more details to set up your account
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <Label htmlFor="firstName" className="text-sm font-medium text-neutral-950">
            First Name
          </Label>
          <Input
            id="firstName"
            type="text"
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
            placeholder="Enter your first name"
            disabled={isLoading}
            className={`h-11 border-neutral-200 focus:ring-black focus:border-black text-neutral-950 ${errors.firstName ? 'border-red-500' : ''}`}
            required
          />
          {errors.firstName && <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>}
        </div>

        <div>
          <Label htmlFor="lastName" className="text-sm font-medium text-neutral-950">
            Last Name
          </Label>
          <Input
            id="lastName"
            type="text"
            value={lastName}
            onChange={e => setLastName(e.target.value)}
            placeholder="Enter your last name"
            disabled={isLoading}
            className={`h-11 border-neutral-200 focus:ring-black focus:border-black text-neutral-950 ${errors.lastName ? 'border-red-500' : ''}`}
            required
          />
          {errors.lastName && <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>}
        </div>
      </div>

      {errors.general && (
        <Alert variant="destructive">
          <AlertDescription>{errors.general}</AlertDescription>
        </Alert>
      )}

      <Button
        type="submit"
        disabled={isLoading}
        className="w-full flex items-center justify-center gap-2 h-11 bg-black hover:bg-gray-800 font-medium"
        size="lg"
      >
        {isLoading ? (
          <>
            <Spinner size={16} color="white" />
            Updating...
          </>
        ) : (
          'Continue'
        )}
      </Button>
    </form>
  );
}
